import React, { useEffect, useState } from 'react';
import { CSS, getTheme } from '../../shared/theme';

const SLIDES = [
  {
    emoji: null, // uses logo
    title: 'Health Intelligence',
    subtitle: 'Your body. Your data. Your growth.',
    detail: 'AI-powered insights built around you.',
    accentHex: '#C8A84B',
  },
  {
    emoji: '⚡',
    title: 'Train. Recover. Rise.',
    subtitle: 'Exercises · Games · AI coaching',
    detail: 'Mental × physical performance — unified in one platform.',
    accentHex: '#7B6FD0',
  },
  {
    emoji: '🌍',
    title: 'Join the movement',
    subtitle: 'Thousands are improving their performance daily.',
    detail: 'Community · Challenges · Real results.',
    accentHex: '#4AC87A',
  },
];

const SLIDE_DURATION = 3800;

function SplashScreen({ onNext, themeId }) {
  const [slide, setSlide]   = useState(0);
  const [visible, setVisible] = useState(true);
  const th = getTheme(themeId || 'gold');

  useEffect(() => {
    const advance = () => {
      setVisible(false);
      setTimeout(() => {
        setSlide(s => {
          if (s >= SLIDES.length - 1) { onNext(); return s; }
          return s + 1;
        });
        setVisible(true);
      }, 380);
    };
    const timer = setTimeout(advance, SLIDE_DURATION);
    return () => clearTimeout(timer);
  }, [slide, onNext]);

  const s = SLIDES[slide];

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(160deg, ${th.bg} 0%, ${th.bg2} 100%)`,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
    }}>
      <style>{CSS + `
        @keyframes sl-spin{to{transform:rotate(360deg)}}
        @keyframes sl-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.05)}}
        @keyframes sl-rise{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes sl-glow{0%,100%{opacity:0.35}50%{opacity:0.7}}
      `}</style>

      {/* Animated background glow */}
      <div style={{
        position: 'absolute', width: 480, height: 480, borderRadius: '50%',
        background: `radial-gradient(circle, ${s.accentHex}18 0%, transparent 70%)`,
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        animation: 'sl-glow 3s ease-in-out infinite',
        transition: 'background 0.6s ease',
        pointerEvents: 'none',
      }} />

      {/* Slide content */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', padding: '0 36px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-14px)',
        transition: 'opacity 0.35s ease, transform 0.35s ease',
      }}>
        {/* Icon / logo */}
        {slide === 0 ? (
          <div style={{ marginBottom: 28, animation: 'sl-pulse 3.5s ease-in-out infinite' }}>
            <img
              src="https://senselifting.com/3d_logo.png"
              alt="Sense Lifting"
              style={{ width: 220 }}
            />
          </div>
        ) : (
          <div style={{
            fontSize: 68, marginBottom: 20, lineHeight: 1,
            filter: `drop-shadow(0 0 18px ${s.accentHex}66)`,
            animation: 'sl-rise 0.4s ease both',
          }}>
            {s.emoji}
          </div>
        )}

        {/* Title */}
        <div style={{
          fontSize: 30, fontWeight: 800, color: '#fff',
          letterSpacing: -0.8, marginBottom: 10, lineHeight: 1.15,
          animation: 'sl-rise 0.4s 0.08s ease both',
        }}>
          {s.title}
        </div>

        {/* Subtitle — accent colored */}
        <div style={{
          fontSize: 15, fontWeight: 600, color: s.accentHex,
          marginBottom: 10, letterSpacing: 0.2,
          animation: 'sl-rise 0.4s 0.16s ease both',
        }}>
          {s.subtitle}
        </div>

        {/* Detail */}
        <div style={{
          fontSize: 13, color: '#6a8099', lineHeight: 1.55,
          animation: 'sl-rise 0.4s 0.24s ease both',
        }}>
          {s.detail}
        </div>
      </div>

      {/* Progress dots */}
      <div style={{
        position: 'absolute', bottom: 56,
        display: 'flex', alignItems: 'center', gap: 7,
      }}>
        {SLIDES.map((_, i) => (
          <div key={i} style={{
            height: 5, borderRadius: 3,
            width: i === slide ? 22 : 5,
            background: i === slide ? s.accentHex : 'rgba(255,255,255,0.18)',
            transition: 'width 0.35s ease, background 0.35s ease',
          }} />
        ))}
      </div>

      {/* Legal */}
      <div style={{
        position: 'absolute', bottom: 20, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', gap: 20,
      }}>
        <a href="/privacy" style={{ color: '#3a4a62', fontSize: 11, textDecoration: 'none' }}>Privacy Policy</a>
        <a href="/terms"   style={{ color: '#3a4a62', fontSize: 11, textDecoration: 'none' }}>Terms &amp; Conditions</a>
      </div>
    </div>
  );
}

export default SplashScreen;
